/**
 * 指文字ドリル1回の進行（reducer）。画面から切り離した純粋な関数。
 * 不正な操作（二重判定・連打など）は、同じ状態をそのまま返して無視する。
 */
import type { FingerspellingEntry, FingerspellingSide } from "./fingerspelling";
import {
  DRILL_QUESTION_COUNT,
  type DrillMode,
  getDrillPool,
  pickQuestions,
} from "./fingerspelling-drill";

export type DrillSettings = {
  mode: DrillMode;
  side: FingerspellingSide;
  /** true なら、苦手な文字だけを出す（よむのみ） */
  weakOnly?: boolean;
};

/** make: prompt → answer、read: asking → correct / incorrect */
export type DrillPhase =
  | "prompt"
  | "answer"
  | "asking"
  | "correct"
  | "incorrect";

export type DrillSession =
  | {
      status: "playing";
      settings: DrillSettings;
      questions: readonly FingerspellingEntry[];
      index: number;
      phase: DrillPhase;
      correctCount: number;
    }
  | {
      status: "finished";
      settings: DrillSettings;
      total: number;
      correctCount: number;
    };

export type DrillAction =
  | { type: "reveal" }
  | { type: "judge"; result: "correct" | "incorrect" }
  | { type: "next" };

const initialPhase = (mode: DrillMode): DrillPhase =>
  mode === "make" ? "prompt" : "asking";

/** questions があれば、それを（先頭から問題数まで）出題にする。なければ出題対象からランダムに選ぶ */
export const createSession = (
  settings: DrillSettings,
  random: () => number = Math.random,
  questions?: readonly FingerspellingEntry[],
): DrillSession => ({
  status: "playing",
  settings,
  questions:
    questions?.slice(0, DRILL_QUESTION_COUNT) ??
    pickQuestions(getDrillPool(settings.mode), DRILL_QUESTION_COUNT, random),
  index: 0,
  phase: initialPhase(settings.mode),
  correctCount: 0,
});

export const reduceSession = (
  state: DrillSession,
  action: DrillAction,
): DrillSession => {
  if (state.status !== "playing") return state;

  switch (action.type) {
    case "reveal":
      return state.phase === "prompt" ? { ...state, phase: "answer" } : state;
    case "judge":
      return state.phase === "asking"
        ? {
            ...state,
            phase: action.result,
            correctCount:
              state.correctCount + (action.result === "correct" ? 1 : 0),
          }
        : state;
    case "next": {
      const answered =
        state.phase === "answer" ||
        state.phase === "correct" ||
        state.phase === "incorrect";
      if (!answered) return state;
      const nextIndex = state.index + 1;
      if (nextIndex >= state.questions.length) {
        return {
          status: "finished",
          settings: state.settings,
          total: state.questions.length,
          correctCount: state.correctCount,
        };
      }
      return {
        ...state,
        index: nextIndex,
        phase: initialPhase(state.settings.mode),
      };
    }
  }
};
